<script lang="ts">
    import {clickOutside} from 'shared/svelte-actions';
    import {getInjector, getLocalization} from 'src/core';
    import KpLoadingBlock from 'shared/components/kp-loading/KpLoadingBlock.svelte';
    import type {Subscription} from 'rxjs';
    import {onDestroy, onMount, tick} from 'svelte';
    import {BarcodeScannerService} from 'shared/barcode-scanner/barcode-scanner.service';
    import {ScannerState} from 'shared/barcode-scanner/barcode-scanner.types';
    import {cleanup, isNullOrUndefined} from 'shared/utils/custom-utils';

    const localize = getLocalization();
    const barcodeScannerService = getInjector().getByClass(BarcodeScannerService);

    // async component init:
    let isRegistered = false; // true after full viewer and scanner initialization
    let isDestroyed = false; // true if component is destroyed

    let openState = false;
    let clickOutsideDisabled = true;
    let hideScannerVideo = true;
    let showLoadingIndicator = false;

    let dialogRef: HTMLDialogElement;
    let videoElementRef: HTMLVideoElement;
    let openStateSubscription: Subscription;
    let scannerStateSubscription: Subscription;

    onMount(async () => {
        if (!await barcodeScannerService.isUsable()) {
            return;
        }

        if (isDestroyed) {
            return;
        }

        const openState$ = await barcodeScannerService.registerScannerViewer(videoElementRef);
        openStateSubscription = openState$.subscribe(handleOpenState);
        scannerStateSubscription = barcodeScannerService.getScannerState$().subscribe(processScannerStateChange);
        isRegistered = true;
    });

    onDestroy(() => {
        isDestroyed = true;

        if (!isRegistered) {
            return;
        }

        cleanup(openStateSubscription, scannerStateSubscription);
        barcodeScannerService.unregisterScannerViewer();
        isRegistered = false;
    });

    const handleOpenState = (desiredOpenState: boolean) => {
        openState = desiredOpenState;
        setTimeout(() => clickOutsideDisabled = !openState, 300); // we need to set clickOutsideDisabled after dialog finishes transition otherwise the handler will immediately close opening dialog
        if (isNullOrUndefined(dialogRef)) {
            return;
        }
        const shouldOpen = desiredOpenState && !dialogRef.open;
        if (shouldOpen) {
            dialogRef.showModal();
            return;
        }

        const shouldClose = !desiredOpenState && dialogRef.open;
        if (shouldClose) {
            dialogRef.close();
            return;
        }
    };

    // TODO: solve this better more systematically
    async function closeViewer() {
        handleOpenState(false); // closes dialog
        await tick(); // wait
        // find and blurs barcode scanner inputs to stop the scanning (make them unsubscribe)
        document.querySelectorAll<HTMLElement>('[data-barcode-scanner]').forEach((scanner) => scanner.blur());
    }

    const processScannerStateChange = (state: ScannerState) => {
        switch (state) {
            case ScannerState.STARTING:
                hideScannerVideo = true;
                showLoadingIndicator = true;
                break;
            case ScannerState.OPERATING:
                hideScannerVideo = false;
                showLoadingIndicator = false;
                break;
            default:
                hideScannerVideo = true;
                showLoadingIndicator = false;
        }
    };
</script>

<dialog bind:this={dialogRef}
        class="barcode-scanner-viewer"
        class:fixed-size={showLoadingIndicator}
        use:clickOutside={{disabled: clickOutsideDisabled}}
        on:click-outside={closeViewer}
        on:cancel={closeViewer}>
    <button class="sr-only" on:click={closeViewer}>
        {localize(/* @kp-localization commons.zavrit */ 'commons.zavrit')}
    </button>
    <div class="transition-wrapper">
        {#if showLoadingIndicator}
            <KpLoadingBlock size="lg" fillAvailableSpace/>
        {/if}

        <div class="scanner-animation-container" class:hidden={hideScannerVideo}>
            <div class="scanner-animation"></div>
        </div>

        <video bind:this={videoElementRef} muted class:hidden={hideScannerVideo}></video>
    </div>
</dialog>

<style lang="less">
    dialog.barcode-scanner-viewer {
        margin-bottom: 0;
        border: 1px solid gray;

        &::backdrop {
            display: none;
        }

        &.fixed-size {
            aspect-ratio: 4/3;
            width: auto;
            height: min(480px, 50dvh);
        }
    }

    video {
        display: block;
        width: 100%;
        max-height: calc(50dvh - 1px);
        height: auto;
    }

    .scanner-animation-container {
        width: 100%;
        height: 100%;
        background-color: transparent;
    }

    .scanner-animation {
        z-index: 1;
        width: 80%;
        left: 10%;
        right: 10%;
        height: 0.83333333333%;
        background-color: red;
        opacity: 0.7;
        position: relative;
        box-shadow: 0 0 8px 10px rgba(170, 11, 23, 0.49);
        top: 50%;

        animation: scan-anim-frames 4s linear infinite;
        will-change: transform;
    }

    @keyframes scan-anim-frames {
        0% {
            transform: translateY(0);
        }
        25% {
            transform: translateY(+4200%);
        }
        75% {
            transform: translateY(-4200%);
        }
    }
</style>